
import Vue from "vue";
import { Alert } from "@/commons/types";
import Alerter from "@/components/Alerter.vue";
import {
  OrganizationsService,
  UserSchema,
  UsersService,
  UserUpdateSchema,
  UserVehicleAccessService,
  UserVehicleAccessSchema,
  VehiclesService,
  VehicleSchema,
} from "@/commons/client";
import PasswordChangeVue from "@/components/PasswordChange.vue";

type Rules = any; // replace with your real Rules type if you have it

export default Vue.extend({
  name: "UserDetailView",
  components: { PasswordChangeVue, Alerter },

  data: () => ({
    alerts: [] as Alert[],
    userData: {} as UserUpdateSchema,
    organizations: [] as { id: number; name: string }[],
    showPasswordForm: false,
    accessibleVehicles: [] as UserVehicleAccessSchema[],
    // ⬇️ must be numbers
    newAccessibleVehicleIds: [] as number[],
    // ⬇️ item-value must match v-model element type (number)
    availableVehicles: [] as { id: number; label: string }[],
    hasFullVehicleAccess: true,
  }),

  computed: {
    rules(): Rules {
      const store = (this as any).$store as { state: { genericRules: Rules } };
      return store?.state?.genericRules ?? {};
    },
    intPk(): number {
      const id = Number((this as any).$route.params.pk);
      return Number.isFinite(id) ? id : NaN;
    },
  },

  watch: {
    hasFullVehicleAccess(val: boolean) {
      if (val) this.newAccessibleVehicleIds = [];
    },
  },

  methods: {
    togglePasswordForm() {
      this.showPasswordForm = !this.showPasswordForm;
    },

    async getUser(): Promise<UserSchema> {
      return await UsersService.userRetrieveV1UsersPkGet(this.intPk);
    },

    async updateUser(): Promise<void> {
      try {
        // format times
        let start = this.userData.access_start_time;
        let end = this.userData.access_end_time;
        if (start && start.length === 5) start += ":00";
        if (end && end.length === 5) end += ":00";

        const payload: UserUpdateSchema = {
          username: this.userData.username,
          email: this.userData.email,
          first_name: this.userData.first_name,
          last_name: this.userData.last_name,
          is_active: this.userData.is_active,
          organization_id: this.userData.organization_id ?? null,
          has_limited_data_access: this.userData.has_limited_data_access,
          access_start_time: start || null,
          access_end_time: end || null,
        };

        const user = await UsersService.userUpdateV1UsersPkPut(
          this.intPk,
          payload
        );
        this.setUser(user);

        // 🚦 vehicle access: must send number[]
        await UserVehicleAccessService.updateUserVehicleAccess(this.intPk, {
          vehicle_ids: this.hasFullVehicleAccess
            ? []
            : this.newAccessibleVehicleIds,
        });

        this.alerts.push({
          type: "success",
          text: "Utente aggiornato con successo",
        });
      } catch (_) {
        this.alerts.push({
          type: "error",
          text: "Errore durante l'aggiornamento dell'utente",
        });
      }
    },

    setUser(user: UserSchema): void {
      this.userData = {
        username: user.username,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        is_active: user.is_active,
        organization_id: user.organization_id ?? null,
        has_limited_data_access: user.has_limited_data_access ?? false,
        access_start_time: user.access_start_time ?? null,
        access_end_time: user.access_end_time ?? null,
      };
    },

    async fetchOrganizations() {
      this.organizations =
        await OrganizationsService.organizationListV1OrganizationsGet(
          undefined,
          100,
          "",
          undefined
        );
    },

    async fetchAvailableVehicles() {
      // ✅ coerce organization_id to number before calling the API
      const orgId = Number(this.userData.organization_id);
      if (!Number.isFinite(orgId)) return;

      const vehicles =
        await OrganizationsService.organizationVehiclesListV1OrganizationsPkVehiclesGet(
          orgId,
          undefined,
          100,
          undefined
        );

      // Ensure numeric ids in the select options
      this.availableVehicles = vehicles.map((v) => ({
        id: Number((v as any).vehicle_id), // cast if OpenAPI types say string
        label: `${(v as any).plate || (v as any).vehicle_id}`,
      }));
    },

    async fetchUserVehicleAccess() {
      const accessList = await UserVehicleAccessService.getUserVehicleAccess(
        this.intPk
      );
      this.accessibleVehicles = accessList;

      // newAccessibleVehicleIds must be number[]
      this.newAccessibleVehicleIds = accessList.map((v) =>
        Number((v as any).vehicle_id)
      );
      this.hasFullVehicleAccess = accessList.length === 0;

      // Fallback: preselect all vehicles when full access and org set and no time limit
      if (
        !this.userData.has_limited_data_access &&
        this.hasFullVehicleAccess &&
        this.userData.organization_id
      ) {
        const orgId = Number(this.userData.organization_id);
        if (!Number.isFinite(orgId)) return;

        const vehicles =
          await OrganizationsService.organizationVehiclesListV1OrganizationsPkVehiclesGet(
            orgId,
            undefined,
            100,
            undefined
          );
        this.newAccessibleVehicleIds = vehicles.map((v) =>
          Number((v as any).vehicle_id)
        );
      }
    },
  },

  props: {
    pk: { type: String, required: true },
  },

  async mounted() {
    try {
      const user = await this.getUser();
      this.setUser(user);
      await Promise.all([
        this.fetchOrganizations(),
        this.fetchUserVehicleAccess(),
        this.fetchAvailableVehicles(),
      ]);
    } catch {
      this.alerts.push({
        type: "error",
        text: "Errore nel caricamento iniziale dell’utente",
      });
    }
  },
});
